
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Avatar } from "@/components/ui/Avatar";
import { VerifiedBadge } from "@/components/ui/VerifiedBadge";
import { VoteButtons } from "@/components/post/VoteButtons";
import { PostActions } from "@/components/post/PostActions";
import { PostMediaPreview } from "@/components/post/PostMediaPreview";
import { TopicPill } from "@/components/post/TopicPill";
import { cn } from "@/lib/utils/cn";
import { formatTimeAgo, formatDateTime, buildProfileUrl } from "@/lib/utils/format";
import type { Post } from "@/types";

interface PostDetailViewProps {
  post: Post & {
    author: {
      id: string; username: string; display_name: string;
      avatar_url: string | null; is_verified: boolean;
      karma_score: number; bio: string | null;
      post_count: number; joined_at: string;
    } | null;
    topic: {
      id: string; name: string; slug: string;
      icon_url: string | null; color: string;
      is_verified: boolean; subscriber_count: number;
      description: string | null; rules: unknown[];
    } | null;
  };
  userId: string | null;
}

export function PostDetailView({ post, userId }: PostDetailViewProps) {
  const [localVote, setLocalVote] = useState(post.user_vote);
  const [localScore, setLocalScore] = useState(post.vote_score);
  const [localBookmarked, setLocalBookmarked] = useState(post.user_bookmarked);

  const postUrl = `/post/${post.id}/${post.slug}`;

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="card p-5"
    >
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-xs text-text-subtle mb-4">
        <Link to="/feed" className="hover:text-foreground transition-colors">Feed</Link>
        <span>/</span>
        {post.topic && (
          <>
            <Link to={`/topics/${post.topic.slug}`} className="hover:text-brand transition-colors">{post.topic.name}</Link>
            <span>/</span>
          </>
        )}
        <span className="text-text-muted truncate max-w-xs">{post.title}</span>
      </div>

      {/* Header row */}
      <div className="flex items-start gap-4">
        {/* Vote column */}
        <div className="flex flex-col items-center pt-1 flex-shrink-0">
          <VoteButtons
            postId={post.id}
            userId={userId}
            currentVote={localVote}
            score={localScore}
            onVoteChange={(v, d) => { setLocalVote(v); setLocalScore(s => s + d); }}
            size="md"
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Meta */}
          <div className="flex items-center gap-1.5 flex-wrap mb-3">
            {post.topic && <TopicPill topic={post.topic} size="sm" />}
            <span className="text-text-subtle">·</span>
            {post.author && (
              <Link to={buildProfileUrl(post.author.username)} className="flex items-center gap-1.5 group">
                <Avatar src={post.author.avatar_url} alt={post.author.display_name} size="xs" />
                <span className="text-xs font-medium text-text-muted group-hover:text-foreground transition-colors">
                  {post.author.display_name}
                </span>
                {post.author.is_verified && <VerifiedBadge size="xs" />}
              </Link>
            )}
            <span className="text-text-subtle">·</span>
            <time className="text-xs text-text-subtle" dateTime={post.published_at} title={formatDateTime(post.published_at)}>
              {formatTimeAgo(post.published_at)}
            </time>
            {post.is_oc && <span className="badge-brand text-2xs">OC</span>}
            {post.is_locked && <span className="badge-muted text-2xs">Locked</span>}
            {post.is_nsfw && <span className="badge-destructive text-2xs">NSFW</span>}
          </div>

          {/* Title */}
          <h1 className="text-xl font-bold text-foreground leading-tight mb-4">
            {post.is_spoiler ? (
              <span className="blur-sm hover:blur-none transition-all duration-200 cursor-pointer select-none"
                title="Click to reveal spoiler">
                {post.title}
              </span>
            ) : post.title}
          </h1>

          {/* Content */}
          {post.content_html ? (
            <div
              className="prose-squids mb-4 text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: post.content_html }}
            />
          ) : post.content ? (
            <p className="text-sm text-foreground leading-relaxed mb-4 whitespace-pre-wrap">{post.content}</p>
          ) : null}

          {/* Link preview */}
          {post.type === "link" && post.link_preview && (
            <a href={post.link_url || "#"} target="_blank" rel="noopener noreferrer"
              className="block mb-4">
              <div className="flex gap-3 rounded-xl border border-border bg-surface-overlay p-3 hover:bg-muted transition-colors group">
                {post.link_preview.image && (
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                    <img src={post.link_preview.image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground group-hover:text-brand transition-colors line-clamp-2">
                    {post.link_preview.title}
                  </p>
                  {post.link_preview.description && (
                    <p className="text-xs text-text-muted mt-1 line-clamp-2">{post.link_preview.description}</p>
                  )}
                  <p className="text-xs text-text-subtle mt-2 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                    {post.link_preview.domain}
                  </p>
                </div>
              </div>
            </a>
          )}

          {/* Media */}
          {post.media && post.media.length > 0 && (
            <div className="mb-4">
              <PostMediaPreview media={post.media} postUrl={postUrl} maxItems={6} />
            </div>
          )}

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {post.tags.map(tag => (
                <Link key={tag} to={`/search?q=${encodeURIComponent(tag)}`}
                  className="text-xs px-2.5 py-1 rounded-full bg-muted text-text-muted hover:bg-brand-muted hover:text-brand transition-colors">
                  #{tag}
                </Link>
              ))}
            </div>
          )}

          {/* Actions */}
          <PostActions
            post={{ ...post, vote_score: localScore, user_vote: localVote }}
            userId={userId}
            localBookmarked={localBookmarked}
            onBookmarkChange={setLocalBookmarked}
            postUrl={postUrl}
          />
        </div>
      </div>
    </motion.article>
  );
}
